import { useAuthenticatedUrl } from "~/hooks/use-authenticated-url";
import cn from "classnames";
import { Dialog, Tooltip } from "~/components/ui";
import { useState, type FunctionComponent } from "react";

type ImageClassNames = Partial<Record<"root" | "image", string>>;

const DEFAULT_CLASS_NAMES: ImageClassNames = {
  root: "max-w-[250px] inline-block align-top mr-2 mb-2",
  image: "max-w-full h-auto rounded-lg cursor-pointer",
};

export const AuthenticatedImage: FunctionComponent<{
  url: string;
  alt: string;
  classNames?: ImageClassNames;
  hoverPreview?: boolean;
}> = ({ url, alt, classNames, hoverPreview }) => {
  const blobUrl = useAuthenticatedUrl(url);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  if (!blobUrl) return null;

  const { root, image: imageClassName } = classNames ?? DEFAULT_CLASS_NAMES;

  // `noa-image` is the hook the carousel CSS uses to centre the image in its slide.
  const image = (
    // stopPropagation: React events also bubble out of the Dialog portal, and must not reach the message's own handlers.
    <span className={cn("noa-image", root)} onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        className="block cursor-pointer rounded-lg border-0 bg-transparent p-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-noa-focus-ring"
      >
        <img src={blobUrl} alt={alt} className={cn("block", imageClassName)} />
      </button>
      <Dialog
        open={isPreviewOpen}
        onCancel={() => setIsPreviewOpen(false)}
        footer={null}
        centered
        width="auto"
        className="max-w-[90vw] p-2"
      >
        <img src={blobUrl} alt={alt} className="block max-h-[85vh] max-w-full object-contain rounded" />
      </Dialog>
    </span>
  );

  if (!hoverPreview) return image;

  return (
    <Tooltip
      title={<img src={blobUrl} alt={alt} className="max-w-60 max-h-60 object-contain rounded" />}
      placement="top"
      className="max-w-none bg-white p-1"
    >
      <span className="inline-block">{image}</span>
    </Tooltip>
  );
};
